import {
  EndpointPermissionAccess,
  EndpointPermissionRule,
  HttpMethod,
} from './endpoint-permission.models';

const SKIP_PATH_PREFIXES = [
  'api/identity/userpermissions',
  'api/identity/login',
  'api/identity/verifyotp',
  'api/identity/resendotp',
  'api/identity/refreshtoken',
  'api/identity/logout',
  'api/identity/userrefreshdata',
  /** Kullanıcının kendi profili — JWT yeterli (api-permission-map: —) */
  'api/profile/',
  'api/authorizationadmin/',
  'api/authorizationmanagement/',
  'api/publiccontent/',
  'api/test/',
  'api/saml',
  'api/sso/',
];

export function normalizeApiPath(url: string): string {
  let path = (url ?? '').trim();
  if (!path) {
    return '';
  }

  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname;
    }
  } catch {
    // relative URL
  }

  path = path.replace(/^\/+/, '').toLowerCase();
  const apiIndex = path.indexOf('api/');
  if (apiIndex > 0) {
    path = path.slice(apiIndex);
  }
  return path.split('?')[0].split('#')[0];
}

export function shouldSkipEndpointPermissionCheck(path: string): boolean {
  if (!path || !path.startsWith('api/')) {
    return true;
  }
  return SKIP_PATH_PREFIXES.some(
    (prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix),
  );
}

export function pathMatchesPattern(path: string, pattern: string): boolean {
  const normalizedPath = normalizeApiPath(path);
  const normalizedPattern = normalizeApiPath(pattern);
  if (!normalizedPath || !normalizedPattern) {
    return false;
  }
  if (normalizedPath === normalizedPattern) {
    return true;
  }
  return normalizedPath.startsWith(`${normalizedPattern}/`);
}

export function findEndpointPermissionRule(
  method: string,
  url: string,
  rules: readonly EndpointPermissionRule[],
  extraRuleCount = 0,
): EndpointPermissionRule | null {
  const path = normalizeApiPath(url);
  if (shouldSkipEndpointPermissionCheck(path)) {
    return null;
  }

  const httpMethod = (method ?? 'GET').toUpperCase() as HttpMethod;

  let bestMatch: EndpointPermissionRule | null = null;
  let bestPatternLength = -1;
  let bestIsExtra = false;

  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const isExtra = i < extraRuleCount;
    if (rule.method !== httpMethod) {
      continue;
    }
    if (!pathMatchesPattern(path, rule.pattern)) {
      continue;
    }
    const patternLength = normalizeApiPath(rule.pattern).length;
    const isBetterLength = patternLength > bestPatternLength;
    const isSameLengthExtraWins =
      patternLength === bestPatternLength && isExtra && !bestIsExtra;
    if (isBetterLength || isSameLengthExtraWins) {
      bestPatternLength = patternLength;
      bestMatch = rule;
      bestIsExtra = isExtra;
    }
  }

  return bestMatch;
}

export function isAccessGranted(
  rule: EndpointPermissionRule,
  hasRead: (key: string) => boolean,
  hasWrite: (key: string) => boolean,
): boolean {
  const keys =
    rule.permissionKeys?.length
      ? rule.permissionKeys
      : rule.permissionKey
        ? [rule.permissionKey]
        : [];

  if (!keys.length) {
    return true;
  }

  const checker = rule.access === 'write' ? hasWrite : hasRead;
  return keys.some(checker);
}
